import {
  CUSTOM_ROUTINE_TRIGGER,
  DaysOfWeek,
  FLAG,
  Mode,
  PackEditorTabMode,
  SUGGESTED_PACK,
  TIMELINE_ROUTINE_KEY,
  TIMELINE_ROUTINE_KEYS
} from 'enums';
import {
  ACTIVITY_TYPES,
  DEFAULT_HABIT_DURATION,
  EMPTY_STRING,
  MAXIMUM_ALLOWED_SECONDS,
  PLATFORMS,
  ROUTINE,
  TAB,
  TIME_FORMAT_24H
} from 'constants/general';
import { WEBVIEW_ROUTES } from 'constants/routes';
import { normalizeHabitName } from 'utils/helpers/normalizeHabitName';
import moment from 'moment';
import emojiRegex from 'emoji-regex';
import { FocusMode } from 'interfaces/focus-mode.interface';
import {
  ActivityType,
  ActivityTypeWithPackTitle,
  FocusModeTemplateType,
  HabitPackType
} from 'interfaces/common.interface';
import { t } from 'services/i18n.service';
import { toast } from 'react-toastify';
import {
  CustomRoutineType,
  RevampTimeSummary,
  SettingsType
} from 'interfaces/setting.interface';
import { compareSettings, isYoutubeURL } from './validators';
import { isCurrentPath } from './routes.util';
import AlarmClock from 'assets/icons/AlarmClock';
import SunSeason from 'assets/icons/SunSeason';
import HotBeverage from 'assets/icons/HotBeverage';
import ShoppingBags from 'assets/icons/ShoppingBags';
import { v4 as uuid } from 'uuid';
import Customize from 'assets/icons/Customize';
import {
  diffInSeconds,
  formatTimeTo12Hour,
  getWrappedForwardDiffInSeconds
} from './time.util';
import {
  Briefcase,
  Moon,
  Sun,
  Zzz,
  GamepadIcon,
  Globe
} from 'assets/icons/revamp';
import COLOR from 'constants/color';

// A break lasts a couple of minutes at the desk, so a break row must not be offered
// morning/evening pack habits. Packs already split their activities by routine, so the
// break editor reads only that bucket.
export const getHabitPackActivitiesWithPackTitle = (
  packs: HabitPackType[],
  activityType?: string
) =>
  packs
    .map((pack) =>
      activityType === ACTIVITY_TYPES.BREAKING
        ? addHabitPackTitleToActivity(
            pack.break_activities ?? [],
            pack.pack_name
          )
        : [
            ...addHabitPackTitleToActivity(
              pack.morning_activities ?? [],
              pack.pack_name
            ),
            ...addHabitPackTitleToActivity(
              pack.break_activities ?? [],
              pack.pack_name
            ),
            ...addHabitPackTitleToActivity(
              pack.evening_activities ?? [],
              pack.pack_name
            )
          ]
    )
    .flat();

export const addHabitPackTitleToActivity = (
  activities: ActivityType[],
  pack_title: string
) => activities.map((activity) => ({ ...activity, pack_title }));

export const getMatchedActivities = (
  marketplaceActivities: ActivityTypeWithPackTitle[],
  activityLibrary: ActivityType[],
  search: string,
  userInput: string
) => {
  const updateSuggestions = [
    ...marketplaceActivities
      .filter((activity) =>
        activity.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
      )
      .sort(
        (activityA, activityB) =>
          activityA.duration_seconds - activityB.duration_seconds
      )
      .map((activity) => ({
        where: SUGGESTED_PACK.HABIT_PACK,
        name: activity.name,
        emoji: activity.habit_icon ?? EMPTY_STRING,
        duration: activity.duration_seconds,
        pack_title: activity.pack_title ?? EMPTY_STRING,
        completionRequirements: activity.completion_requirements ?? undefined
      })),
    ...(activityLibrary ?? [])
      .filter((activity) =>
        activity.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
      )
      .sort(
        (activityA, activityB) =>
          activityA.duration_seconds - activityB.duration_seconds
      )
      .map((activity) => ({
        where: SUGGESTED_PACK.LIBRARY,
        name: activity.name,
        emoji: activity.habit_icon ?? EMPTY_STRING,
        duration: activity.duration_seconds,
        completionRequirements: activity.completion_requirements ?? undefined
      }))
  ];
  return updateSuggestions?.length
    ? [
        {
          duration: DEFAULT_HABIT_DURATION,
          name: normalizeHabitName(userInput ?? EMPTY_STRING),
          emoji: userInput ? '✍️' : EMPTY_STRING,
          where: SUGGESTED_PACK.CREATE_NEW_HABIT
        },
        ...updateSuggestions
      ]
    : [];
};

export const getHighPriorityRoutineDurationAsSecond = (
  activities?: ActivityType[]
) =>
  (activities ?? []).reduce(
    (duration, activity) =>
      !activity.show_saved_distracting_websites
        ? duration + activity.duration_seconds
        : duration,
    0
  );

export const getRoutineDurationAsSecond = (activities?: ActivityType[]) =>
  (activities ?? []).reduce(
    (duration, activity) => duration + (activity.duration_seconds ?? 0),
    0
  );

export const addMinutesToTime = (time: string, minutes: number): string => {
  const [hours, mins] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(mins + minutes);
  const newHours = date.getHours();
  const newMinutes = date.getMinutes();
  return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
};

export const convertSecondsToMinutes = (seconds: number) => {
  return Math.floor(seconds / 60);
};

export const checkActivityDurationAgainstRelaxFocusModeDuration = (
  inputDuration: number,
  user_focus_modes: FocusMode[],
  cutoff_time: string,
  shutdown_time?: string,
  evening_activities?: ActivityType[],
  currentActivity?: ActivityType
) => {
  const evening_activity = evening_activities?.find(
    (activity) => activity.id === currentActivity?.id
  );
  const isRelaxFocusModeSelected = user_focus_modes.some(
    (focus_mode) =>
      (focus_mode?.name?.includes(t('relax')) ||
        focus_mode?.metadata?.isRelaxFocus) &&
      currentActivity?.allowed_focus_mode_id === focus_mode?.id
  );
  const isGuiltyFreePopUpEnabled =
    currentActivity?.show_saved_distracting_websites;
  if (
    cutoff_time &&
    evening_activity &&
    isRelaxFocusModeSelected &&
    isGuiltyFreePopUpEnabled
  ) {
    const relaxFocusModeDuration = moment(cutoff_time, TIME_FORMAT_24H).diff(
      moment(shutdown_time, TIME_FORMAT_24H),
      'seconds'
    );
    inputDuration > relaxFocusModeDuration &&
      toast.warn(
        t('relax_mode_habit_duration_warning', {
          cutoff_time: moment(cutoff_time, TIME_FORMAT_24H).format('hh:mm a'),
          max_duration: moment.duration(inputDuration, 'seconds').asMinutes()
        }),
        {
          pauseOnHover: true
        }
      );
  }
};

export const filterRoutinesInvalidActivities = (
  morning: ActivityType[],
  micro_breaks: ActivityType[],
  evening: ActivityType[],
  customRoutines?: CustomRoutineType[],
  shouldMigrateSettingsToWeb?: boolean
) => {
  const morning_activities = removeRoutineInvalidActivities(
    morning ?? [],
    shouldMigrateSettingsToWeb
  );
  const break_activities = removeRoutineInvalidActivities(
    micro_breaks ?? [],
    shouldMigrateSettingsToWeb
  );
  const evening_activities = removeRoutineInvalidActivities(
    evening ?? [],
    shouldMigrateSettingsToWeb
  );
  const custom_routines = customRoutines
    ?.filter((routine) =>
      routine.name && routine.trigger === CUSTOM_ROUTINE_TRIGGER.ON_SCHEDULE
        ? routine.start_time && routine.end_time && routine.days_of_week?.length
        : true
    )
    .map((routine) => ({
      ...routine,
      standalone_activities: removeRoutineInvalidActivities(
        routine.standalone_activities ?? [],
        shouldMigrateSettingsToWeb
      )
    }));
  return {
    morning_activities,
    break_activities,
    evening_activities,
    custom_routines
  };
};

const removeRoutineInvalidActivities = (
  activities: ActivityType[],
  shouldMigrateSettingsToWeb?: boolean
) =>
  activities.filter((activity) => {
    const hasCompletionRequirements =
      typeof activity.completion_requirements === 'string' &&
      activity.completion_requirements.trim().length > 0;

    if (!normalizeHabitName(activity.name ?? '')) {
      return false;
    }

    if (shouldMigrateSettingsToWeb) {
      return (
        activity.duration_seconds <= MAXIMUM_ALLOWED_SECONDS ||
        hasCompletionRequirements
      );
    }

    return Boolean(
      activity.duration_seconds &&
      (activity.duration_seconds <= MAXIMUM_ALLOWED_SECONDS ||
        hasCompletionRequirements)
    );
  });

export const getSettingTabIcon = (tabTitle: string) => {
  switch (tabTitle) {
    case TAB.TIMING:
      return <AlarmClock fill='currentColor' fillDarkMode='currentColor' />;
    case TAB.MORNING:
      return <SunSeason fill='currentColor' fillDarkMode='currentColor' />;
    case TAB.MICRO_BREAKS:
      return <HotBeverage fill='currentColor' fillDarkMode='currentColor' />;
    case TAB.EVENING:
      return <Moon fill='currentColor' fillDarkMode='currentColor' />;
    case TAB.HABIT_PACK:
      return <ShoppingBags fill='currentColor' fillDarkMode='currentColor' />;
    case TAB.APPS_AND_URLS:
      return <Globe fill='currentColor' fillDarkMode='currentColor' />;
    case TAB.TEMPLATE_INFO:
    case TAB.PACK_INFO:
    case TAB.FOCUS_MODE_INFO:
      return <Customize fill='currentColor' fillDarkMode='currentColor' />;
    default:
      return <Customize fill='currentColor' fillDarkMode='currentColor' />;
  }
};

//@Description, break temporary disabled for mobile apps
//@Description: custom routines are available to all non-mobile users (previously web-admin-only);
// on mobile they remain gated behind the CUSTOM_ROUTINES_DEV_MODE flag.
export const getRoutineTabs = (
  tabs: string[],
  platform: string,
  isGeekMode: boolean,
  isUserAdmin: boolean,
  customRoutine?: string,
  flags?: FLAG[]
) => {
  let routineTabs: string[] = [];
  if (!customRoutine && PLATFORMS.WEB !== platform) {
    if (!isGeekMode && isMobilePlatform(platform)) {
      routineTabs = flags?.includes(FLAG.CUSTOM_ROUTINES_DEV_MODE)
        ? tabs.filter((tab) => ![TAB.MICRO_BREAKS].includes(tab))
        : tabs.filter(
            (tab) => ![TAB.MICRO_BREAKS, TAB.CUSTOM_ROUTINES].includes(tab)
          );
    } else if (isMobilePlatform(platform)) {
      routineTabs = isUserAdmin
        ? tabs.slice()
        : tabs.filter((tab) => ![TAB.CUSTOM_ROUTINES].includes(tab));
    } else {
      routineTabs = tabs.slice();
    }
  } else {
    routineTabs = tabs.slice();
  }
  return routineTabs;
};

export const getCopiedActivity = (activity: ActivityType) => ({
  ...activity,
  name: normalizeHabitName(
    t('copy_of_activity_name', { activity_name: activity.name })
  ),
  log_quantity_questions:
    activity?.log_quantity_questions?.map((question) => ({
      ...question,
      id: uuid()
    })) || []
});

export const getRoutineTriggerOptions = (custom_routine_name: string) => [
  {
    label: t('on_demand'),
    value: CUSTOM_ROUTINE_TRIGGER.ON_DEMAND,
    tooltip: t('tooltip_custom_routine_on_demand', {
      custom_routine_name
    })
  },
  {
    label: t('on_a_schedule'),
    value: CUSTOM_ROUTINE_TRIGGER.ON_SCHEDULE,
    tooltip: t('tooltip_custom_routine_schedule', {
      custom_routine_name
    })
  }
];

export const dayOfWeekLabels = {
  [DaysOfWeek.SUN]: 'days.sunday',
  [DaysOfWeek.MON]: 'days.monday',
  [DaysOfWeek.TUE]: 'days.tuesday',
  [DaysOfWeek.WED]: 'days.wednesday',
  [DaysOfWeek.THU]: 'days.thursday',
  [DaysOfWeek.FRI]: 'days.friday',
  [DaysOfWeek.SAT]: 'days.saturday'
};

/** Short labels for compact day toggles (e.g. More Options weekly schedule). */
export const dayOfWeekAbbrevLabels: Record<DaysOfWeek, string> = {
  [DaysOfWeek.MON]: 'days.monday_short',
  [DaysOfWeek.TUE]: 'days.tuesday_short',
  [DaysOfWeek.WED]: 'days.wednesday_short',
  [DaysOfWeek.THU]: 'days.thursday_short',
  [DaysOfWeek.FRI]: 'days.friday_short',
  [DaysOfWeek.SAT]: 'days.saturday_short',
  [DaysOfWeek.SUN]: 'days.sunday_short'
};

export const updateSelectedDaysOfWeek = (days: string[]) =>
  days?.map((day) => ({
    label: dayOfWeekLabels[day as keyof typeof DaysOfWeek],
    value: day
  }));

export const DAYS_OF_WEEK_OPTIONS = Object.values(DaysOfWeek).map((day) => ({
  label: dayOfWeekLabels[day as keyof typeof DaysOfWeek],
  value: day
}));

export const isMobilePlatform = (platform: string) =>
  [PLATFORMS.ANDROID, PLATFORMS.IOS, PLATFORMS.MOB].includes(platform);

export const shouldShowGlobalCutoffTime = (
  flags: FLAG[],
  platform: string
): boolean =>
  platform !== PLATFORMS.WEB
    ? flags.includes(FLAG.GEEK_MODE) &&
      isCurrentPath(WEBVIEW_ROUTES.WEBVIEW_SETTINGS)
    : true;

export const shouldAllowAddFreeTimeHabit = ({
  flags,
  globalCutoffTime,
  shutdown_time,
  evening_activities,
  userFocusModes,
  platform
}: {
  flags: FLAG[];
  globalCutoffTime?: string;
  shutdown_time?: string;
  evening_activities?: ActivityType[];
  userFocusModes?: FocusMode[];
  platform: string;
}): boolean => {
  if (!shouldShowGlobalCutoffTime(flags, platform)) {
    return false;
  }

  const cutoffTime = globalCutoffTime;
  const hasValidTimeRange =
    Boolean(cutoffTime) && diffInSeconds(cutoffTime, shutdown_time ?? '') > 0;
  const hasFocusModes = Boolean(userFocusModes?.length);
  const allEveningActivitiesValid = evening_activities?.every(
    ({ allowed_focus_mode_id, show_saved_distracting_websites }) =>
      !allowed_focus_mode_id && !show_saved_distracting_websites
  );

  return Boolean(
    hasValidTimeRange && hasFocusModes && allEveningActivitiesValid
  );
};

export const getTimeSummaries = (currentSettings: SettingsType) => {
  const summaries = [];
  const {
    morning_activities,
    evening_activities,
    startup_time,
    shutdown_time
  } = currentSettings;

  const duration_of_morning_routine =
    getRoutineDurationAsSecond(morning_activities);

  const duration_of_evening_routine =
    getRoutineDurationAsSecond(evening_activities);

  const morningRoutineStartTime = formatTimeTo12Hour(startup_time);
  const end_time = formatTimeTo12Hour(shutdown_time);

  const morningRoutineEndTime = startup_time
    ? formatTimeTo12Hour(
        addMinutesToTime(
          startup_time,
          convertSecondsToMinutes(duration_of_morning_routine)
        )
      )
    : '';

  const eveningRoutineEndTime = formatTimeTo12Hour(
    shutdown_time,
    duration_of_evening_routine
  );

  if (startup_time) {
    const hasMorningActivities = Boolean(morning_activities?.length);
    summaries.push({
      title: hasMorningActivities
        ? t('summary.morning_routine_duration', {
            morningRoutineStartTime,
            morningRoutineEndTime
          })
        : t('summary.empty_morning_routine'),
      tooltip: hasMorningActivities
        ? t('summary.tool_tip_morning_routine_duration')
        : ''
    });
  }

  const hasEveningActivities = Boolean(evening_activities?.length);

  summaries.push(
    {
      title: t('summary.work_time', { morningRoutineEndTime, end_time }),
      tooltip: t('summary.tool_tip_work_time')
    },
    {
      title: hasEveningActivities
        ? t('summary.evening_routine_duration', {
            cutOffTime: end_time,
            eveningRoutineEndTime
          })
        : t('summary.empty_evening_routine'),
      tooltip: hasEveningActivities
        ? t('summary.tool_tip_evening_routine_duration')
        : ''
    },
    {
      title: t('summary.end_of_day_sleep_time', {
        eveningRoutineEndTime,
        morningRoutineStartTime
      }),
      tooltip: t('summary.tool_tip_end_of_day_sleep_time')
    }
  );

  return summaries;
};

export const excludeFreeTimeFromRevampSummaries = (
  summaries: RevampTimeSummary[]
): RevampTimeSummary[] =>
  summaries.filter(
    (summary) => summary.routineKey !== TIMELINE_ROUTINE_KEY.FREE_TIME
  );

export const getRevampTimeSummaries = (
  currentSettings: SettingsType
): RevampTimeSummary[] => {
  const orderSummariesChronologically = (
    items: RevampTimeSummary[]
  ): RevampTimeSummary[] =>
    [...items].sort(
      (a, b) =>
        TIMELINE_ROUTINE_KEYS.indexOf(a.routineKey) -
        TIMELINE_ROUTINE_KEYS.indexOf(b.routineKey)
    );

  const summaries: RevampTimeSummary[] = [];
  const {
    morning_activities,
    evening_activities,
    startup_time,
    shutdown_time,
    cutoff_time_for_non_high_priority_activities: cutoff_time
  } = currentSettings;

  const scheduledEveningActivities = (evening_activities ?? []).filter(
    (activity) => !activity.show_saved_distracting_websites
  );

  const duration_of_morning_routine =
    getRoutineDurationAsSecond(morning_activities);

  const duration_of_evening_routine = getRoutineDurationAsSecond(
    scheduledEveningActivities
  );

  const morningRoutineStartTime = formatTimeTo12Hour(startup_time);
  const end_time = formatTimeTo12Hour(shutdown_time);
  const cutoffTimeFormatted = formatTimeTo12Hour(cutoff_time);

  const morningRoutineEndTime = startup_time
    ? formatTimeTo12Hour(
        addMinutesToTime(
          startup_time,
          convertSecondsToMinutes(duration_of_morning_routine)
        )
      )
    : '';

  const eveningRoutineEndTime = formatTimeTo12Hour(
    shutdown_time,
    duration_of_evening_routine
  );

  const hasEveningActivities = scheduledEveningActivities.length > 0;

  const hasValidCutoff =
    Boolean(cutoff_time) &&
    Boolean(shutdown_time) &&
    moment(cutoff_time, TIME_FORMAT_24H).isValid();

  const freeTimeDurationSeconds = getWrappedForwardDiffInSeconds(
    cutoff_time,
    shutdown_time
  );

  const showFreeTime =
    hasValidCutoff && freeTimeDurationSeconds > duration_of_evening_routine;

  const freeTimeStartTime = hasEveningActivities
    ? eveningRoutineEndTime
    : end_time;

  const sleepStartTime = hasValidCutoff
    ? cutoffTimeFormatted
    : eveningRoutineEndTime;

  if (startup_time) {
    summaries.push({
      title: `${morningRoutineStartTime} - ${morningRoutineEndTime}`,
      routineKey: TIMELINE_ROUTINE_KEY.MORNING_ROUTINE,
      routine: t('settings_page.morning_routine'),
      icon: (
        <Sun
          className='~w-5/6 ~h-5/6'
          fill={COLOR.PRIMARY.LIGHT_400}
          fillDarkMode={COLOR.PRIMARY.DARK_500}
        />
      ),
      color: 'bg-primary-50 dark:bg-secondary-dark-50'
    });
  }

  summaries.push({
    title: `${morningRoutineEndTime} - ${end_time}`,
    tooltip: t('summary.tool_tip_work_time'),
    routineKey: TIMELINE_ROUTINE_KEY.WORK,
    routine: t('settings_page.work'),
    icon: (
      <Briefcase
        className='~w-4/6 ~h-4/6'
        fill={COLOR.PRIMARY.LIGHT_500}
        fillDarkMode={COLOR.PRIMARY.LIGHT_500}
      />
    ),
    color: 'bg-primary-100 dark:bg-secondary-dark-400'
  });

  if (hasEveningActivities) {
    summaries.push({
      title: `${end_time} - ${eveningRoutineEndTime}`,
      tooltip: t('summary.tool_tip_evening_routine_duration'),
      routineKey: TIMELINE_ROUTINE_KEY.EVENING_ROUTINE,
      routine: t('settings_page.evening_routine'),
      icon: (
        <Moon
          className='~w-4/5 ~h-4/5'
          fill={COLOR.PRIMARY.LIGHT_100}
          fillDarkMode={COLOR.PRIMARY.LIGHT_50}
        />
      ),
      color: 'bg-primary-600 dark:bg-secondary-dark-600'
    });
  }

  if (showFreeTime) {
    summaries.push({
      title: `${freeTimeStartTime} - ${cutoffTimeFormatted}`,
      tooltip: t('summary.tool_tip_free_time'),
      routineKey: TIMELINE_ROUTINE_KEY.FREE_TIME,
      routine: t('settings_page.free_time'),
      icon: (
        <GamepadIcon
          className='~w-4/6 ~h-4/6'
          fill={COLOR.PRIMARY.LIGHT_50}
          fillDarkMode={COLOR.PRIMARY.LIGHT_50}
        />
      ),
      color: 'bg-primary-800 dark:bg-primary-dark-400'
    });
  }

  summaries.push({
    title: `${sleepStartTime} - ${morningRoutineStartTime}`,
    tooltip: t('summary.tool_tip_end_of_day_sleep_time'),
    routineKey: TIMELINE_ROUTINE_KEY.SLEEP,
    routine: t('settings_page.sleep'),
    icon: (
      <Zzz
        className='~w-4/5 ~h-4/5'
        fill={COLOR.PRIMARY.LIGHT_100}
        fillDarkMode={COLOR.PRIMARY.LIGHT_50}
      />
    ),
    color: 'bg-primary-900 dark:bg-primary-dark-200'
  });

  return orderSummariesChronologically(summaries);
};

export const isValidateFocusModeTemplate = (
  currentViewedFocusTemplate: FocusModeTemplateType | null,
  isTextEditorWithinCharacterLimit: boolean
) => {
  const areAllRequiredFieldsFilled =
    currentViewedFocusTemplate?.name &&
    currentViewedFocusTemplate?.description &&
    currentViewedFocusTemplate?.welcome_message;
  const areDescriptionUrlsValid =
    (currentViewedFocusTemplate?.description_video_url ?? '')
      ? true
      : isYoutubeURL(String(currentViewedFocusTemplate?.description_video_url));
  const areWelcomeUrlsValid =
    (currentViewedFocusTemplate?.welcome_video_url ?? '')
      ? true
      : isYoutubeURL(String(currentViewedFocusTemplate?.welcome_video_url));

  return Boolean(
    areAllRequiredFieldsFilled &&
    areDescriptionUrlsValid &&
    areWelcomeUrlsValid &&
    isTextEditorWithinCharacterLimit &&
    (currentViewedFocusTemplate?.allowed_urls?.length ||
      currentViewedFocusTemplate?.allowed_apps?.length)
  );
};

export type SettingsContentMode = Mode | PackEditorTabMode;

export const resolveSettingsContentMode = (
  mode: Mode,
  packEditorTabMode: PackEditorTabMode | null
): SettingsContentMode => packEditorTabMode ?? mode;

export const hasUserEditedSettings = (
  currentSettings: SettingsType,
  previousSettings: SettingsType
): boolean => compareSettings(currentSettings, previousSettings);

export const hasUserSettingsPayload = (payload: unknown): boolean => {
  if (payload === null || payload === undefined) {
    return false;
  }
  if (typeof payload !== 'object') {
    return false;
  }
  return Object.keys(payload as Record<string, unknown>).length > 0;
};

export function parseNonNegativeInteger(value: string) {
  const parsedValue = Math.floor(parseInt(value, 10));
  return Number.isInteger(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
}

export function isValidNonNegativeInteger(value: string) {
  const parsedValue = Math.floor(parseInt(value, 10));
  return Number.isInteger(parsedValue) && parsedValue >= 0;
}

export function isValidEmojiInput(value: string) {
  if (value === undefined || value === null) {
    return false;
  }
  const parsedValue = value.trim();
  if (parsedValue === '') {
    return true;
  }
  const regex = emojiRegex();
  // Check if the entire input is a single emoji
  return (
    regex.test(parsedValue) && parsedValue.match(regex)?.[0] === parsedValue
  );
}

export const isEditingSpecificRoutine = (customRoutine: string) => {
  return [ROUTINE.MORNING, ROUTINE.EVENING].includes(customRoutine);
};
